import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { Cookie, X, Settings2, ShieldCheck } from "lucide-react";

const KEY = "img.cookie-consent";

const DEFAULT_CHOICES = { essential: true, analytics: false, marketing: false };

export function getConsent() {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch (_) { return null; }
}

export function setConsent(choices) {
  const record = { accepted: true, choices: { ...DEFAULT_CHOICES, ...choices }, timestamp: new Date().toISOString() };
  localStorage.setItem(KEY, JSON.stringify(record));
  window.dispatchEvent(new CustomEvent("img-consent-change", { detail: record }));
  return record;
}

export function openCookiePreferences() {
  window.dispatchEvent(new CustomEvent("img-open-cookie-prefs"));
}

export default function CookieConsent() {
  // Defer ALL rendering until after first effect so the server-rendered HTML
  // (captured during prerender) consistently shows nothing, matching the
  // first hydrated render which also runs with mounted=false. Prevents a
  // hydration mismatch where prerender captured a visible banner that the
  // client's first render wouldn't reproduce.
  const [mounted, setMounted] = useState(false);
  const [visible, setVisible] = useState(false);
  const [customize, setCustomize] = useState(false);
  const [choices, setChoices] = useState(DEFAULT_CHOICES);

  useEffect(() => {
    setMounted(true);
    const existing = getConsent();
    if (!existing) setVisible(true);
    else setChoices(existing.choices || DEFAULT_CHOICES);
    const reopen = () => { setCustomize(true); setVisible(true); };
    window.addEventListener("img-open-cookie-prefs", reopen);
    return () => window.removeEventListener("img-open-cookie-prefs", reopen);
  }, []);

  const acceptAll = useCallback(() => {
    setConsent({ essential: true, analytics: true, marketing: true });
    setVisible(false); setCustomize(false);
  }, []);
  const rejectAll = useCallback(() => {
    setConsent({ essential: true, analytics: false, marketing: false });
    setVisible(false); setCustomize(false);
  }, []);
  const savePrefs = useCallback(() => {
    setConsent(choices);
    setVisible(false); setCustomize(false);
  }, [choices]);

  if (!mounted || !visible) return null;
  return (
    <div className="fixed inset-x-4 bottom-4 z-[80] flex justify-center pointer-events-none" data-testid="cookie-consent">
      <div className="pointer-events-auto w-full max-w-[720px] rounded-2xl bg-navy text-white shadow-card border border-white/10 p-6 animate-fade-up">
        <div className="flex items-start gap-3">
          <Cookie className="h-5 w-5 text-teal-light shrink-0 mt-0.5" />
          <div className="flex-1">
            <h2 className="font-display font-semibold text-lg">We value your privacy</h2>
            <p className="mt-2 text-sm text-white/80 leading-relaxed">
              We use cookies to keep this site secure, measure how it performs and improve your experience. Essential cookies are always on. You can accept all, reject non-essential, or customise your choices. Read our <Link to="/cookie-policy" className="text-teal-light underline">Cookie Policy</Link>.
            </p>

            {customize && (
              <div className="mt-5 grid gap-3" data-testid="cookie-customize">
                {[
                  { k: "essential", title: "Essential", body: "Required for the site to function (security, session, consent).", disabled: true },
                  { k: "analytics", title: "Analytics", body: "Helps us understand which pages are useful so we can improve them." },
                  { k: "marketing", title: "Marketing", body: "Used to measure outreach campaigns. None enabled today; off by default." },
                ].map((c) => (
                  <label key={c.k} className="flex items-start gap-3 p-3 rounded-xl bg-white/5 border border-white/10 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={c.disabled ? true : choices[c.k]}
                      disabled={c.disabled}
                      onChange={(e) => setChoices((s) => ({ ...s, [c.k]: e.target.checked }))}
                      className="mt-1 h-4 w-4 accent-teal"
                      data-testid={`cookie-toggle-${c.k}`}
                    />
                    <div>
                      <p className="text-sm font-semibold">{c.title}{c.disabled && <span className="ml-2 text-[10px] uppercase tracking-wider text-teal-light">always on</span>}</p>
                      <p className="text-xs text-white/65">{c.body}</p>
                    </div>
                  </label>
                ))}
              </div>
            )}

            <div className="mt-5 flex flex-wrap items-center gap-2">
              {!customize ? (
                <>
                  <button onClick={acceptAll} data-testid="cookie-accept-all" className="px-5 py-2.5 rounded-full bg-teal text-white text-sm font-semibold hover:bg-teal-light hover:text-navy transition-colors">
                    Accept all
                  </button>
                  <button onClick={rejectAll} data-testid="cookie-reject" className="px-5 py-2.5 rounded-full bg-white/10 text-white text-sm font-semibold hover:bg-white/15 transition-colors">
                    Reject non-essential
                  </button>
                  <button onClick={() => setCustomize(true)} data-testid="cookie-customize-btn" className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-full text-white/85 text-sm hover:text-teal-light">
                    <Settings2 className="h-4 w-4" /> Customise
                  </button>
                </>
              ) : (
                <>
                  <button onClick={savePrefs} data-testid="cookie-save" className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-full bg-teal text-white text-sm font-semibold hover:bg-teal-light hover:text-navy transition-colors">
                    <ShieldCheck className="h-4 w-4" /> Save preferences
                  </button>
                  <button onClick={acceptAll} className="px-5 py-2.5 rounded-full bg-white/10 text-white text-sm font-semibold hover:bg-white/15">
                    Accept all
                  </button>
                </>
              )}
            </div>
          </div>
          <button onClick={rejectAll} data-testid="cookie-close" aria-label="Reject non-essential cookies and close" className="p-1.5 rounded-full hover:bg-white/10 text-white/70">
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
